import { Alert, Grid, Group, Stack } from "@mantine/core";
import { Ref, FC } from "react";
import Text from "@/components/atoms/Text";
import { Contract, ContractCompanySpecificService } from "@/types";
import { useTranslation } from "react-i18next";
import { getContractCardDetails, getContractTradeType } from "@/utils/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import { formatAddress } from "@/utils/AddressHelper";
import Divider from "@/components/atoms/Divider";
import dayjs from "dayjs";
import { useGetBoxtypeValues, useGetCreateOrderValues } from "@/services/Orders";
import { useGetCompanySpecificServices } from "@/services/Company";
import { CreateOrderFromContractValues } from "..";
import { getContractDeliveryAddresses } from "../helpers";

interface Props {
  onValidationSuccess: (data: any) => void;
  wizardData: CreateOrderFromContractValues;
  /** The services that will be sent with the order, with any price the user has edited already applied. */
  services: ContractCompanySpecificService[];
  contract: Contract;
  showLowCreditWarning: boolean;
  ref?: Ref<HTMLFormElement>;
}

const VerificationStep: FC<Props & { ref?: React.Ref<HTMLFormElement> }> = ({
  onValidationSuccess,
  showLowCreditWarning,
  wizardData,
  services,
  contract,
  ref,
}) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const { sellerName, buyerName } = getContractCardDetails(contract!);
  const { data: createOrderValues } = useGetCreateOrderValues();
  const { data: boxTypes } = useGetBoxtypeValues();

  const { data: companySpecificServicesData } = useGetCompanySpecificServices(
    contract?.createdByCompany as number,
    false,
    {
      enabled: !!contract?.createdByCompany,
    }
  );

  const contractTradeType = getContractTradeType(contract!, currentCompany);
  const counterpartAddress = formatAddress(
    wizardData.selectedCounterpartAddress ??
      getContractDeliveryAddresses(contract, contractTradeType).counterpartAddress
  );

  return (
    <form ref={ref} onSubmit={onValidationSuccess}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <Text size="sm" color="secondary" data-testid={"product-label"}>
              {t("product:product")}
            </Text>
            <Text size="lg" strong={true} lineClamp={2} data-testid={"product-name"}>
              {contract?.contractProduct.companySpecificProduct.companySpecificProductName}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <Text size="sm" color="secondary" data-testid={"contract-label"}>
              {t("contracts:contract")}
            </Text>
            <Text size="lg" strong={true} lineClamp={2} data-testid={"contract-name"}>
              {contract?.name}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <Text size="sm" color="secondary" data-testid={"trading-partner-label"}>
              {t("contracts:tradingPartner")}
            </Text>
            <Text size="lg" strong={true} lineClamp={2} data-testid={"trading-partner-name"}>
              {contractTradeType === ContractTradeTypeEnum.BUY ? sellerName : buyerName}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <Text size="sm" color="secondary" data-testid={"estimated-delivery-label"}>
              {t("contracts:estimatedDelivery")}
            </Text>
            <Text size="lg" strong={true} data-testid={"estimated-delivery-value"}>
              {wizardData?.estimatedDelivery ? dayjs(wizardData.estimatedDelivery).format("DD.MM.YYYY") : "-"}
              {", "}
              {wizardData?.amount} {createOrderValues?.units.find((unit) => wizardData.unit === unit.id)?.value}
              {", "}
              {boxTypes?.find((boxType) => wizardData.boxtypeId === boxType.id)?.description}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack justify="flex-start">
            {/* YOUR COMPANY */}
            <Group p={0} gap={0} bg="transparent" align="center">
              <Text color="secondary" size="sm" data-testid={"company-delivery-location-label"}>
                {contractTradeType === ContractTradeTypeEnum.BUY
                  ? t("contracts:deliverTo")
                  : t("contracts:deliverFrom")}
              </Text>
            </Group>
            <Text size="lg" strong={true} data-testid={"company-delivery-location-value"}>
              {wizardData.address}, {wizardData.postCode} {wizardData.place}
            </Text>
            {wizardData.latitude && wizardData.longitude && (
              <Text size="sm" color="secondary" data-testid={"pickup-coordinates-value"}>
                {t("orders:pickupCoordinates")}: {wizardData.latitude}, {wizardData.longitude}
              </Text>
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          {/* THEIR COMPANY */}
          <Stack>
            <Group p={0} gap={0} bg="transparent" align="center">
              <Text color="secondary" size="sm" data-testid={"counterpart-delivery-location-label"}>
                {contractTradeType === ContractTradeTypeEnum.BUY
                  ? t("contracts:deliverFrom")
                  : t("contracts:deliverTo")}
              </Text>
            </Group>
            {counterpartAddress && (
              <Text size="lg" strong={true} data-testid={"counterpart-delivery-location-value"}>
                {counterpartAddress}
              </Text>
            )}
          </Stack>
        </Grid.Col>

        {services.length > 0 && (
          <>
            <Grid.Col span={12}>
              <Divider />
            </Grid.Col>
            <Grid.Col span={12}>
              <Stack>
                <Text color="secondary" size="sm" data-testid={"services-label"}>
                  {t("common:services")}
                </Text>
                <Stack gap="md">
                  {services.map((service) => {
                    const _serviceDetails = companySpecificServicesData?.find(
                      (s) => s.id === service.companySpecificServiceId
                    );

                    return (
                      <Group
                        key={service.companySpecificServiceId}
                        gap="sm"
                        align="center"
                        data-testid={`service-${service.companySpecificServiceId}`}
                        style={{
                          padding: "10px",
                          border: "1px solid var(--color-border-secondary)",
                          borderRadius: "8px",
                          backgroundColor: "var(--color-background-secondary)",
                          flexWrap: "nowrap", // Prevent wrapping
                          flexShrink: 0, // Prevent shrinking
                          flexGrow: 0, // Prevent growing
                          minWidth: "150px", // Optional: Set a minimum width
                          width: "fit-content",
                        }}
                      >
                        <Stack gap={0} style={{ flex: "1 1 auto" }}>
                          <Text size="sm" color="secondary">
                            + {_serviceDetails?.name}
                          </Text>
                          <Text size="sm">
                            {service.price} {service.currencyType}/
                            {t("enumerables:invoicePaymentIntervals." + _serviceDetails?.priceUnit.code)}
                          </Text>
                        </Stack>
                      </Group>
                    );
                  })}
                </Stack>
              </Stack>
            </Grid.Col>
          </>
        )}

        {contract.tradeAtOwnRisk && (
          <Alert color="warning" title={t("contracts:creditWarningTitle")} data-testid={"trade-at-own-risk"}>
            {t("contracts:creditWarningDetailsPage")}
          </Alert>
        )}
        {showLowCreditWarning && (
          <Text pt="10px" color="warning" data-testid={"low-credit-warning"}>
            {t("contracts:counterPartLowCreditWarning")}
          </Text>
        )}
      </Grid>
    </form>
  );
};

export default VerificationStep;
